import { useEffect, useMemo, useState } from "react";
import styles from "./LoginWithEmailOtpSlide.module.css";
import { ISignInProcessInitWithUrlDto } from "@presentation/controllersModels/ISignInProcessInitWithUrlDto";
import EmailSlide from "./OtpCode/EmailSlide";
import OtpSlide from "./OtpCode/OtpSlide";
import classNames from "classnames";
import { generateOtp, loginWithOtp } from "@view/common/Api/LoginApi";
import { useNotificationModal } from "@view/common/Components/NotificationModal/useNotificationModal";
import NotificationModal from "@view/common/Components/NotificationModal";
import { SadFlower } from "../Components/Icons/SadFlower";
import { useLoginDestiny } from "./useLoginDestiny";
import { Megaphone } from "../Components/Icons/Megaphone";

export interface ILoginWithEmailOtpFormValue {
  password: string;
}

export interface ILoginWithEmailOtpSlideProps {
  onSuccess: (sessionInfo: ISignInProcessInitWithUrlDto) => void;
  className?: string;
  sliderPosition: boolean;
  setSliderPosition: (position: boolean) => void;
}

const LoginWithEmailOtpSlide = (props: ILoginWithEmailOtpSlideProps) => {

  const { notificationModalProps, openNotificationModal } = useNotificationModal();
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loadingOtp, setLoadingOtp] = useState(false);
  const [email, setEmail] = useState("");
  const [otpId, setOtpId] = useState<string | null>(null);
  const loginDestiny = useLoginDestiny();
  const [resetForm, setResetForm] = useState(false);
  
  useEffect(() => { if (resetForm === true) setResetForm(false); }, [resetForm]);

  const _className = useMemo(() => (
    [
      styles.root,
      props.className,
    ].filter(x => x !== undefined).join(" ")
  ), [props.className]);

  const callGenerateOtpApi = async (email: string, isResend?: boolean) => {
    setLoadingOtp(true);
    const response = await generateOtp({ email });
    setLoadingOtp(false);
    if (!response.isSuccess) {
      if (response.status == 401) {
        openNotificationModal({
          disableCloseButton: true,
          title: "No se pudo enviar el código",
          actionLabel: "Cerrar",
          image: () => <SadFlower />,
          variant: "custom",
          description: `Verifica que hayas escrito correctamente tu correo electrónico.`
        });
      } else {
        openNotificationModal({
          disableCloseButton: true,
          title: "No se pudo enviar el código",
          actionLabel: "Cerrar",
          image: () => <SadFlower />,
          variant: "custom",
          description: `Parece que algo no salió como esperábamos. Verifica tus credenciales e inténtalo nuevamente. Si el problema persiste, no dudes en contactarnos para recibir ayuda. ¡Estamos aquí para ayudarte a entrar!`
        });
      }
      return;
    }
    setOtpId(response.body.id);

    !isResend && setEmail(email);
    !isResend && props.setSliderPosition(!props.sliderPosition);

    if (isResend) {
      setResetForm(true);
      openNotificationModal({
        disableCloseButton: true,
        title: "Revisa tu correo electrónico",
        actionLabel: "Cerrar",
        image: () => <Megaphone />,
        variant: "custom",
        description: `Se ha generado un nuevo código, por favor ingrésalo en el campo "Código".`
      });
    }
  };

  const callLoginApi = async (password: string) => {
    if (otpId === null) return;
    setLoadingLogin(true);
    const response = await loginWithOtp({
      oneTimePasswordId: otpId,
      password,
      destiny: loginDestiny,
      email: email
    });
    if (!response.isSuccess) {
      setLoadingLogin(false);
      if (response.status == 401) {
        openNotificationModal({
          disableCloseButton: true,
          title: "Código incorrecto",
          actionLabel: "Cerrar",
          image: () => <SadFlower />,
          variant: "custom",
          description: `El código ingresado no es válido. Por favor, revisa e intenta nuevamente.`
        });
      }
      else if (response.status == 403) {
        openNotificationModal({
          disableCloseButton: true,
          title: "Código incorrecto",
          actionLabel: "Cerrar",
          image: () => <SadFlower />,
          variant: "custom",
          description: `El código ingresado no es válido. Por favor, revisa e intenta nuevamente.`
        });
      } else {
        openNotificationModal({
          disableCloseButton: true,
          title: "Error inesperado",
          actionLabel: "Cerrar",
          image: () => <SadFlower />,
          variant: "custom",
          description: `Ocurrió un error inesperado al tratar de iniciar sesión`
        });
      }
      return;
    }
    props.onSuccess(response.body);
  };

  return (
    <div className={_className}>
      <EmailSlide
        className={classNames({ [styles.hidden]: props.sliderPosition })}
        loading={loadingOtp}
        callGenerateOtpApi={callGenerateOtpApi}
      />
      <OtpSlide
        className={classNames({ [styles.hidden]: !props.sliderPosition })}
        onSuccess={props.onSuccess}
        loading={loadingLogin}
        loadingOtp={loadingOtp}
        callGenerateOtpApi={callGenerateOtpApi}
        callLoginApi={callLoginApi}
        email={email}
        reset={resetForm}
      />
      <NotificationModal {...notificationModalProps} />
    </div>
  );

};

export default LoginWithEmailOtpSlide;
